
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, XCircle, Eye, FileText, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { useIsMobile } from '@/hooks/use-mobile';

interface Certification {
  id: string;
  practitioner_id: string;
  certificate_name: string;
  issuing_organization: string;
  certification_type: string;
  issue_date: string;
  expiry_date: string | null;
  description: string | null;
  file_url: string;
  file_type: string;
  file_name: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface CertificationReviewProps {
  certification: Certification;
  onStatusUpdate: () => void;
}

const CertificationReview: React.FC<CertificationReviewProps> = ({ 
  certification, 
  onStatusUpdate 
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const getFileUrl = (fileUrl: string) => {
    // If it's already a full URL, return as is
    if (fileUrl.startsWith('http')) {
      return fileUrl;
    }
    
    // Generate public URL for stored file
    const { data } = supabase.storage.from('practitioner_files').getPublicUrl(fileUrl);
    return data.publicUrl;
  };

  const updateCertificationStatus = async (newStatus: 'approved' | 'rejected') => {
    setIsUpdating(true);
    try {
      console.log(`Updating certification ${certification.id} to ${newStatus}`);
      
      // Update the certification status
      const { error: certError } = await supabase
        .from('practitioner_certifications')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', certification.id);

      if (certError) {
        console.error('Error updating certification:', certError);
        throw certError;
      }

      // If approved, also update the practitioner's verification status and profile status
      if (newStatus === 'approved') {
        console.log(`Setting practitioner ${certification.practitioner_id} as verified and approved`);
        
        const { error: practitionerError } = await supabase
          .from('practitioners')
          .update({ 
            is_verified: true,
            profile_status: 'approved',
            updated_at: new Date().toISOString()
          })
          .eq('id', certification.practitioner_id);

        if (practitionerError) {
          console.error('Error updating practitioner verification and status:', practitionerError);
          // Don't throw here - certification update was successful
          toast({
            title: 'Partial Success',
            description: 'Certification approved but failed to update practitioner status.',
            variant: 'destructive'
          });
        } else {
          console.log('Successfully updated practitioner verification and profile status');
        }
      }

      toast({
        title: `Certification ${newStatus}`,
        description: `The certification has been ${newStatus} successfully.`,
      });

      onStatusUpdate();
    } catch (error) {
      console.error('Error updating certification status:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update certification status.',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 flex items-center gap-1 border-green-200">
            <CheckCircle className="h-3 w-3" /> Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 flex items-center gap-1 border-red-200">
            <XCircle className="h-3 w-3" /> Rejected
          </Badge>
        );
      case 'pending_review':
      default:
        return (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 flex items-center gap-1 border-amber-200">
            <AlertTriangle className="h-3 w-3" /> Pending Review
          </Badge>
        );
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg font-medium">{certification.certificate_name}</CardTitle>
            <CardDescription>{certification.issuing_organization}</CardDescription>
          </div>
          {getStatusBadge(certification.status)}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500">Type</p>
            <p className="text-sm">{certification.certification_type}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Issue Date</p>
            <p className="text-sm">{format(new Date(certification.issue_date), 'MMM dd, yyyy')}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Expiry Date</p>
            <p className="text-sm">
              {certification.expiry_date ? format(new Date(certification.expiry_date), 'MMM dd, yyyy') : 'No expiry'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Submitted</p>
            <p className="text-sm">{format(new Date(certification.created_at), 'MMM dd, yyyy')}</p>
          </div>
        </div>

        {certification.description && (
          <div className="mb-4">
            <p className="text-xs text-gray-500">Description</p>
            <p className="text-sm">{certification.description}</p>
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          {isMobile ? (
            <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Eye className="h-4 w-4 mr-1" /> View Certificate
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-[95vw] max-h-[95vh] p-0">
                <DialogHeader className="p-4 pb-2">
                  <DialogTitle>Certificate Preview</DialogTitle>
                  <DialogDescription>
                    {certification.certificate_name} - {certification.issuing_organization}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex-1 p-4 pt-0">
                  <iframe
                    src={getFileUrl(certification.file_url)}
                    className="w-full h-[70vh] border-0 rounded"
                    title="Certificate Preview"
                  />
                </div>
              </DialogContent>
            </Dialog>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(getFileUrl(certification.file_url), '_blank')}
            >
              <Eye className="h-4 w-4 mr-1" /> View Certificate
            </Button>
          )}

          {certification.status === 'pending_review' && (
            <>
              <Button
                size="sm"
                onClick={() => updateCertificationStatus('approved')}
                disabled={isUpdating}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve
              </Button>

              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={isUpdating}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Reject Certification</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to reject this certification? This action cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Reason for rejection (optional)</label>
                      <Textarea
                        placeholder="Provide reason for rejection..."
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <DialogTrigger asChild>
                        <Button variant="outline">Cancel</Button>
                      </DialogTrigger>
                      <Button
                        variant="destructive"
                        onClick={() => updateCertificationStatus('rejected')}
                        disabled={isUpdating}
                      >
                        Reject Certification
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CertificationReview;